/**
 * @fileoverview Pure helpers for turning a `course/{courseId}` document's
 * date fields into the strings course copy needs. Kept free of hooks and
 * Firestore so they can be unit tested and reused by any surface that has
 * already loaded the course document.
 */

/** Minimum age, in years, to enroll in or teach a Code in Place course. */
const MINIMUM_AGE_YEARS = 18;

/**
 * Parses one of a course document's date fields at local midnight.
 *
 * These fields are ISO 8601 strings (YYYY-MM-DDTHH:mm:ssXXX). Only the calendar
 * date is used, and it is parsed at local midnight, so the displayed day does
 * not shift by the viewer's timezone.
 *
 * @param isoDate - The raw field value, or undefined when the course document
 *   has none set.
 * @return The parsed Date, or null when `isoDate` is missing or unparseable.
 * @throws Never — an unparseable value yields null.
 */
function parseCourseDate(isoDate?: string): Date | null {
  if (!isoDate) {
    return null;
  }
  const parsed = new Date(`${isoDate.substring(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Formats a course document date in the viewer's locale (e.g. "October 12,
 * 2026").
 *
 * @param isoDate - The raw field value, or undefined when the course document
 *   has none set.
 * @return The formatted date, or '' when `isoDate` is missing or unparseable.
 * @throws Never — see the return contract.
 */
function formatCourseDate(isoDate?: string): string {
  const parsed = parseCourseDate(isoDate);
  if (!parsed) {
    return '';
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Formats a course start date for display in the viewer's locale (e.g.
 * "October 12, 2026").
 *
 * @param startDate - The course document's `startDate`, or undefined when the
 *   document has none set.
 * @return The formatted date, or '' when `startDate` is missing or
 *   unparseable. Callers should render date-free fallback copy in that case.
 * @throws Never — see the return contract.
 */
export function formatCourseStartDate(startDate?: string): string {
  return formatCourseDate(startDate);
}

/**
 * Formats an application deadline (`studentAppDueDate` / `slAppDueDate`) for
 * display, so the "applications close" copy follows the admin editor instead of
 * being retyped each cycle.
 *
 * @param dueDate - The course document's due-date field, or undefined when the
 *   document has none set.
 * @return The formatted date, or '' when `dueDate` is missing or unparseable.
 *   Callers should render deadline-free copy in that case.
 * @throws Never — see the return contract.
 */
export function formatApplicationDueDate(dueDate?: string): string {
  return formatCourseDate(dueDate);
}

/**
 * Formats the latest birth date that still satisfies the course's minimum-age
 * requirement — the date exactly {@link MINIMUM_AGE_YEARS} years before the
 * course starts. Used by the "you must be 18" copy so the concrete date stays
 * correct across cycles instead of being retyped each year.
 *
 * The boundary is inclusive of the returned date: someone born exactly on it
 * turns 18 on the first day of the course and qualifies. Copy that renders this
 * should therefore read "born on or before <date>".
 *
 * @param startDate - The course document's `startDate`, or undefined when the
 *   document has none set.
 * @return The formatted cutoff date in the viewer's locale, or '' when
 *   `startDate` is missing or unparseable. Callers should render age-only copy
 *   ("be at least 18 years old") without the date in that case.
 * @throws Never — see the return contract.
 */
export function formatMinimumAgeBirthDate(startDate?: string): string {
  const parsed = parseCourseDate(startDate);
  if (!parsed) {
    return '';
  }
  const cutoff = new Date(parsed);
  cutoff.setFullYear(cutoff.getFullYear() - MINIMUM_AGE_YEARS);
  return cutoff.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
